import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AvatarBoError } from '../../shared/AvatarBoClient'
import { shouldRetry } from '../../shared/retry'
import type { AvatarBoClient, SnapshotProfileInput } from '../../shared/types'

export const snapshotProfileKeys = {
  profile: () => ['avatar-bo', 'snapshot-profile'] as const,
}

export function useSnapshotProfile(client: AvatarBoClient) {
  const qc = useQueryClient()
  const [apiError, setApiError] = useState<string | null>(null)

  const query = useQuery(
    snapshotProfileKeys.profile(),
    () => client.getSnapshotProfile(),
    { refetchOnWindowFocus: false, retry: shouldRetry },
  )

  const save = useMutation(
    (input: SnapshotProfileInput) => client.setSnapshotProfile(input),
    {
      onMutate: () => setApiError(null),
      onSuccess: () => {
        qc.invalidateQueries(snapshotProfileKeys.profile())
      },
      onError: (error: unknown) => {
        // The API's own sentence, not a generic one: it names the offending pair ("32 follows 64",
        // "1024 at 2x is 2048 raster pixels"), which is the whole reason the rules live there.
        setApiError(
          error instanceof AvatarBoError && error.issues.length
            ? error.issues.map((i) => i.detail ?? i.code).join(' ')
            : error instanceof Error
              ? error.message
              : String(error),
        )
      },
    },
  )

  return { query, save, apiError }
}
